import React, { useState } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import { getAndSaveConsumerMetadata } from '@atc/consumer-metadata';
import { useFeatures } from '@atc/features';

import {
    Box,
    BoxGrid,
    Col,
    LazyRender,
    Row,
    SubHeading,
    Tab,
    Tabs,
    Text,
    Timeline,
} from 'reaxl';
import { KBBDealIndicator } from 'reaxl-molecules';

import { priceAdvisorText } from 'axl-config';

import globalConfigsDuck from '@/ducks/globalConfigsDuck.js';

import PriceDropAlertsFetcher from '@/fetchers/vdp/PriceDropAlertsFetcher';

import EmailSubscribeForm from '@/components/EmailSubscribeForm';
import PriceAdvisor from '@/components/PriceAdvisor';
import PricingBreakdownOffers from '@/components/vehicledetails/PricingBreakdownOffers';

const getPriceAdvisorSubHeader = (isNewCar, msrp, hasStatePricingRules) => {
    const subHeader = priceAdvisorText.priceAdvisorSubHeader;
    if (!isNewCar) {
        return hasStatePricingRules ? subHeader.hasStatePricingRules.used : subHeader.used;
    }
    if (msrp > 0) {
        return hasStatePricingRules ? subHeader.hasStatePricingRules.new : subHeader.new;
    }
    return subHeader.unavailable;
};

/**
 *  The PricingDetails component is a component that leverages the PricingBreakdown, PriceAdvisor component to display price data.
 *
 */
function PricingDetails({
    showAskingPrice = false,
    listing,
    showPriceBreakdown = true,
    showPriceAdvisor = true,
    showDealerAdjustment,
    zip,
    alignment = 'center',
    activeKey = 1,
    emailCTAProps,
}) {

    const device = useDevice();
    const isGreaterThanSm = device?.greaterThan?.sm ?? false;
    const isLessThanLg = device?.lessThan?.lg ?? false;

    const {
        est_payments: [, {
            makes_excluded_from_dealer_offers: makesExcludedFromDealerOffers,
        }],
        email_opt_in: [isEmailOptInEnabled],
        price_history: [isPriceHistoryEnabled],
        brand: [, { consumer_brand: consumerBrand }],
    } = useFeatures([
        'est_payments',
        'email_opt_in',
        'price_history',
        'brand',
    ]);

    const [showPriceAlertError, setShowPriceAlertError] = useState(false);
    const [showPriceAlertSuccess, setShowPriceAlertSuccess] = useState(false);
    const priceAdvisorApiUrl = useSelector(globalConfigsDuck.selectors.getPriceAdvisorApiUrl);
    const priceAdvisorApiKey = useSelector(globalConfigsDuck.selectors.getPriceAdvisorApiKey);

    const { id, makeCode = '', owner, pricingDetail = {}, title, type = '', vin = '' } = listing;
    const privateSeller = _get(listing, 'owner.privateSeller');
    const priceAdvisorRuleEnabled = _get(listing, 'priceAdvisorRuleEnabled');
    const { dealIndicator, msrp } = pricingDetail;
    const greatPriceIndicator = dealIndicator === 'Great';
    const hasPriceHistory = isPriceHistoryEnabled && listing?.pricingHistory?.length > 0;

    if (makesExcludedFromDealerOffers?.includes(makeCode)) {
        delete pricingDetail.reductions;
        delete pricingDetail.discounts;
    }

    const isTabVisible = showPriceAdvisor && showPriceBreakdown;
    const hasSingleSection = !showPriceBreakdown || !showPriceAdvisor;
    const isLeftAligned = alignment === 'left';
    const sharedClassNames = clsx({
        'col-xs-12': true,
        'col-sm-12': isTabVisible,
        'col-sm-offset-1': isTabVisible && !isLeftAligned,
        'col-md-12': isTabVisible,
        'col-md-offset-2': isTabVisible && !isLeftAligned,
        'col-lg-12': isTabVisible,
        'col-lg-offset-1': isTabVisible && !isLeftAligned,
        'col-sm-6': !showPriceAdvisor,
        'col-md-5': !isTabVisible && !hasSingleSection && !isLeftAligned,
        'padding-vertical-5': isTabVisible,
        'padding-left-sm-0': isLeftAligned,
    });

    const renderDealIndicator = (tab) => dealIndicator && (
        <Box
            backgroundColor="neutral-primary-transparent-40"
            borderRadius="inset"
            className={clsx('padding-3', {
                'margin-bottom-5': !hasPriceHistory || tab === 'price-breakdown',
            })}
        >
            <KBBDealIndicator
                className="margin-bottom-2"
                pricingDetail={pricingDetail}
                flat
            />
            <Text size="300">
                The dealer&apos;s listed price is in the
                {`${greatPriceIndicator ? ' lower ' : ' upper '}`}
                half of the Kelley Blue Book® Fair Market Range. This is based on cars in your area similar to the one you want. The dealer provides the vehicle information and listing price, which may not include all fees.
            </Text>
        </Box>
    );

    const onPriceAlertSubmit = async (data) => {
        const hasValidData = !!(id && owner?.id && title);
        const response = hasValidData && (await PriceDropAlertsFetcher({
            ...data,
            listingId: id,
            ownerId: owner.id,
            bookmarkTitle: title,
        }));

        await getAndSaveConsumerMetadata({
            email: data.emailAddress,
            brand: consumerBrand,
            shouldUpdateConsumerMetadata: true,
        });

        if ((!hasValidData || response?.responseCode !== '200')) {
            setShowPriceAlertError(true);
        } else {
            setShowPriceAlertSuccess(true);
        }
    };

    const renderEmailSubscribe = () => isEmailOptInEnabled && (
        <EmailSubscribeForm
            title="Sign Up for Price Alerts"
            borderRadius="inset"
            onSubmit={onPriceAlertSubmit}
            registrationError={showPriceAlertError ? 'Due to a server error, we were unable to complete your request to receive price alerts. Please try again later.' : undefined}
            registrationSuccess={showPriceAlertSuccess ? "Success! You'll now receive price alerts." : undefined}
        />
    );

    const getPriceBreakdown = (centered = hasSingleSection) => {

        const textAlignment = centered ? 'text-center' : 'text-left';
        const sellerType = privateSeller ? 'private seller' : 'dealer';
        let pricingDetailsCopy = '';
        if (isTabVisible || (!showPriceAdvisor && showPriceBreakdown)) {
            pricingDetailsCopy = `Contact the ${sellerType} for further pricing details.`;
        }

        return (
            <div
                className={clsx({ 'margin-bottom-4 margin-bottom-sm-0': !hasSingleSection }, sharedClassNames, textAlignment)}
            >
                <SubHeading level={2}>
                    {isTabVisible || !showPriceAdvisor && showPriceBreakdown ? 'Price Breakdown and Offers' : ''}
                </SubHeading>
                <p className="margin-top-2 margin-bottom-5">{pricingDetailsCopy}</p>
                <Row>
                    <Col
                        xs={12}
                        md={6}
                        className={clsx({
                            'padding-horizontal-2': !isGreaterThanSm,
                            'padding-right-3': isGreaterThanSm,
                        })}
                    >
                        <Box
                            backgroundColor="neutral-primary-transparent-40"
                            className={clsx({ 'margin-bottom-5': !isGreaterThanSm })}
                            borderRadius="inset"
                        >
                            <PricingBreakdownOffers
                                pricingDetail={pricingDetail}
                                hasOffer={listing.hasSpecialOffer}
                                showDealerAdjustment={showDealerAdjustment}
                                emailCTAProps={!listing?.owner?.hideEmailForm ? emailCTAProps : undefined}
                            />
                        </Box>
                    </Col>
                    <Col
                        xs={12}
                        md={6}
                        className={clsx({
                            'padding-horizontal-2': !isGreaterThanSm,
                            'padding-left-3': isGreaterThanSm,
                        })}
                    >
                        {renderDealIndicator('price-breakdown')}
                        {renderEmailSubscribe()}
                    </Col>
                </Row>
            </div>
        );
    };

    const renderPriceAdvisor = () => showPriceAdvisor && (
        <Box
            backgroundColor="neutral-primary-transparent-40"
            className="display-flex justify-content-center align-items-center"
            borderRadius="inset"
        >
            <PriceAdvisor
                advisorType={privateSeller ? 'privateparty' : 'fairpurchaseprice'}
                apiUrl={priceAdvisorApiUrl}
                clientId={priceAdvisorApiKey}
                height={isLessThanLg || !hasPriceHistory ? '308' : '372'}
                listingType={type}
                showAskingPrice={showAskingPrice}
                vin={vin}
                zip={zip}
            />
        </Box>
    );

    const renderPriceHistory = () => {
        if (!hasPriceHistory) return null;

        const pricingHistory = [];
        listing?.pricingHistory?.slice(-4)?.map((item) => {
            pricingHistory.push({ label: item.dateUpdated, value: item.price });
        });

        return (
            <Timeline
                backgroundColor="neutral-primary-transparent-40"
                header="Vehicle Pricing History"
                data={pricingHistory}
                className="margin-bottom-5"
                borderRadius="inset"
            />
        );
    };

    const renderPriceDifferentiatorContent = () => (
        <LazyRender height={472.5}>
            <div
                className={clsx(alignment === 'center' ? 'text-center' : 'text-left', {
                    'margin-top-5': isTabVisible,
                })}
            >
                <SubHeading level={2}>
                    {privateSeller ? 'Kelley Blue Book® Private Party Range ' : 'Kelley Blue Book® Fair Market Range'}
                </SubHeading>
                <p className="margin-top-2 margin-bottom-5">{getPriceAdvisorSubHeader(type.toLowerCase() === 'new', msrp, priceAdvisorRuleEnabled)}</p>
                <BoxGrid
                    className="gap-5"
                    breakpoint="lg"
                >
                    <div>
                        {renderPriceAdvisor()}
                        {!showPriceAdvisor && renderDealIndicator()}
                        {(!dealIndicator && !showPriceAdvisor) && renderPriceHistory()}
                    </div>
                    <div>
                        {(dealIndicator || showPriceAdvisor) && renderPriceHistory()}
                        {(!hasPriceHistory && showPriceAdvisor) && renderDealIndicator()}
                        {renderEmailSubscribe()}
                    </div>
                    {hasPriceHistory && showPriceAdvisor && renderDealIndicator()}
                </BoxGrid>
            </div>
        </LazyRender>
    );

    const renderTabView = () => (
        <Tabs
            defaultActiveKey={activeKey}
            id="pricing-tabs"
            className="nav-tabs-light nav-tabs-container"
            alignment={isLeftAligned ? 'left' : 'center'}
        >
            <Tab
                eventKey={1}
                title="Fair Market Range"
            >
                {renderPriceDifferentiatorContent()}
            </Tab>
            <Tab
                eventKey={2}
                title="Price Breakdown and Offers"
            >

                {getPriceBreakdown(!isLeftAligned)}
            </Tab>
        </Tabs>
    );

    return isTabVisible ? renderTabView() : renderPriceDifferentiatorContent();
}

PricingDetails.propTypes = {
    /**
     * The alignment of the content, tab
     */
    alignment: PropTypes.oneOf(['left', 'center']),
    /**
     * This boolean determines whether the asking price plot shows on price advisor iframe.
     */
    showAskingPrice: PropTypes.bool,
    /**
     * The listing object has all of the data specific to the listing/seller.
     */
    listing: PropTypes.object,
    /**
     * This boolean determines whether PriceBreakdown is shown or not.
     */
    showPriceBreakdown: PropTypes.bool,
    /**
     * This boolean determines whether PriceAdivisor is shown or not.
     */
    showPriceAdvisor: PropTypes.bool,
    /**
     * This is the owner's zip code that will be appended to the price advisor url as a request param
     */
    zip: PropTypes.string,
    /**
     * This is the value to set active tab for tabbed pricing details
     */
    activeKey: PropTypes.number,
};

export default PricingDetails;
